import { AxiosError } from "axios";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import type {
  InstructionBulkApply,
  InstructionTarget,
  InstructionType,
  UnitInstructions
} from "types/landlord/address";
import { getErrorMessage } from "utils/error";

export type PropertyDictionary = ReturnType<
  typeof useUnitsPropertiesDictionary
>;

export const MAX_INSTRUCTION_TARGETS = 200;

const readStoredPerPage = () => {
  try {
    return Number(window.localStorage.getItem("per_page")) || 10;
  } catch {
    return 10;
  }
};

export const INSTRUCTION_TARGETS_PER_PAGE = readStoredPerPage();

export const INSTRUCTION_TYPES: InstructionType[] = ["entry", "exit"];

export const INSTRUCTION_TYPE_LABELS: Record<InstructionType, string> = {
  entry: "Entry instructions",
  exit: "Exit instructions"
};

export const unitLabel = (count: number, dictionary: PropertyDictionary) =>
  count === 1 ? dictionary.unit : dictionary.units;

export const propertyLabel = (count: number, dictionary: PropertyDictionary) =>
  count === 1 ? dictionary.property : dictionary.properties;

export const getUnitName = (
  target: Pick<InstructionTarget, "name">,
  dictionary: PropertyDictionary
) => target.name || `Unnamed ${dictionary.unit}`;

export const getPropertyName = (
  target: Pick<InstructionTarget, "property_name">,
  dictionary: PropertyDictionary
) => target.property_name || `Unnamed ${dictionary.property}`;

export const getTargetSource = (
  target: InstructionTarget,
  type: InstructionType
) => (type === "entry" ? target.entry_source : target.exit_source);

export const isTargetOverridden = (
  target: InstructionTarget,
  types: InstructionType[]
) => types.some((type) => getTargetSource(target, type) === "custom");

export const isTypeAvailable = (
  instructions: UnitInstructions | null | undefined,
  type: InstructionType
) => instructions?.[type]?.state === "custom";

export const getSourcePreview = (
  instructions: UnitInstructions | null | undefined,
  type: InstructionType,
  dictionary: PropertyDictionary
) => {
  if (!isTypeAvailable(instructions, type))
    return `This ${dictionary.unit} uses the template's ${INSTRUCTION_TYPE_LABELS[type].toLowerCase()}.`;

  return (
    instructions?.[type]?.unit_text?.trim() ||
    instructions?.[type]?.text?.trim() ||
    "No text saved."
  );
};

export const isSettledInstructionStatus = (
  status: InstructionBulkApply["status"]
) => status === "completed" || status === "failed" || status === "cancelled";

export const isInstructionRunActive = (
  run: object | null,
  status: InstructionBulkApply | undefined
) => run !== null && !(status && isSettledInstructionStatus(status.status));

const APPLY_ERROR_MESSAGES: Record<
  string,
  (dictionary: PropertyDictionary) => string
> = {
  run_in_progress: () =>
    "A previous bulk apply run is still in progress — wait for it to finish or cancel it before starting another.",
  batch_size_exceeded: (dictionary) =>
    `You can apply to at most ${MAX_INSTRUCTION_TARGETS} ${dictionary.units} at a time.`,
  source_not_custom: (dictionary) =>
    `This ${dictionary.unit} no longer has its own instructions for the selected types.`,
  already_settled: () => "This run has already finished.",
  not_cancellable: () => "This run can't be cancelled."
};

const getInstructionErrorCode = (error: unknown) =>
  error instanceof AxiosError
    ? (error.response?.data?.error?.code as string | undefined)
    : undefined;

export const getApplyErrorMessage = (
  error: unknown,
  dictionary: PropertyDictionary
) => {
  const code = getInstructionErrorCode(error);
  const resolver = code ? APPLY_ERROR_MESSAGES[code] : undefined;

  return resolver ? resolver(dictionary) : getErrorMessage(error);
};
